/**
 * Company registration-number lookup, shared by every place that asks for one.
 *
 * Neither registry source allows a browser origin (dan.metacog.mn sends no
 * Access-Control-Allow-Origin; ebarimt's is hardcoded to developer.itc.gov.mn), so this
 * goes through our own proxy, which resolves the name server-side. The proxy is a Vercel
 * serverless function (api/ebarimt-lookup.js) with a matching local-dev route in
 * src/setupProxy.js.
 */

export const EBARIMT_PROXY_URL = "/api/ebarimt-lookup";

// The proxy's own worst case is ~16s on Vercel (dan.metacog.mn, 10s + one 6s retry) and
// ~19s locally (7s, then two sequential ebarimt calls of 6s each) — api/_ebarimtLookup.js. This has to sit above that or it would cut the proxy off
// before its own safety net gets a chance to answer cleanly —
// but it still has to end, or a stalled connection (no TCP reset, just silence) leaves
// `fetch` pending forever and the caller's regState/busy flag stuck on "checking" with
// no error ever shown, which is indistinguishable from the button doing nothing.
const LOOKUP_TIMEOUT_MS = 20000;

/**
 * @param {string} regNo 7-8 digit registration number
 * @returns {Promise<{found: boolean, name?: string, error?: string}>}
 *   `error: "unreachable"` means the proxy itself failed (offline, timed out, or a dev
 *   server started before setupProxy.js gained the route) — callers fall back to
 *   letting the employer type the company name by hand rather than blocking them.
 */
export const lookupCompanyByRegNo = async (regNo) => {
  const value = String(regNo || "").trim();
  if (value.length < 7) return { found: false, error: "invalid_regno" };

  const url = `${EBARIMT_PROXY_URL}?regNo=${value}`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LOOKUP_TIMEOUT_MS);
  try {
    const res = await fetch(url, { headers: { Accept: "application/json" }, signal: controller.signal });

    // Read as text first. The dev server's SPA fallback answers this path with
    // index.html when the proxy route is missing, and res.json() threw on the "<" —
    // indistinguishable from the registry being down unless the body is kept.
    const raw = await res.text();
    let data;
    try {
      data = JSON.parse(raw);
    } catch {
      console.error("[companyLookup] proxy returned non-JSON", {
        url,
        status: res.status,
        body: raw.slice(0, 200),
      });
      return { found: false, error: "unreachable" };
    }

    if (data?.found && data?.name) return { found: true, name: data.name };

    // The proxy's own "upstream_error" (it reached ebarimt but the call itself
    // failed) is the same situation as our client-side "unreachable" — the
    // registry didn't answer, not that the number is wrong. Fold it into the
    // same bucket so callers fall back to manual entry instead of a false
    // "not registered".
    const error = data?.error === "upstream_error" ? "unreachable" : (data?.error || "not_found");
    if (error === "unreachable") {
      // `detail` is filled in by api/_ebarimtLookup.js with the real upstream failure.
      console.error("[companyLookup] registry unreachable", {
        regNo: value,
        status: res.status,
        detail: data?.detail || "(no detail — proxy is an older build)",
      });
    }
    return { found: false, error };
  } catch (err) {
    // Never reached the proxy at all: offline, CORS, or our own timeout above fired —
    // AbortError covers both, and there is no way to tell them apart from here, but the
    // one thing that matters to the caller (unreachable, fall back to manual entry) is
    // the same either way.
    console.error(
      err?.name === "AbortError"
        ? "[companyLookup] request timed out"
        : "[companyLookup] request failed before any reply",
      { url, timeoutMs: LOOKUP_TIMEOUT_MS, name: err?.name, message: err?.message }
    );
    return { found: false, error: "unreachable" };
  } finally {
    clearTimeout(timer);
  }
};
